"use client";

import { useEffect, useRef } from "react";
import { useSignalRConnection } from "./SignalRContext";

/**
 * Subscribe to a named SignalR hub event for the lifetime of the component.
 *
 * The handler reference is kept fresh via a ref so callers do not need to
 * memoize it — only the event name and connection identity trigger
 * re-subscription.
 */
export function useSignalREvent<T = unknown>(
  eventName: string,
  handler: (payload: T) => void,
): void {
  const connection = useSignalRConnection();
  const handlerRef = useRef(handler);
  // eslint-disable-next-line react-hooks/refs -- keep callback stable without handler in deps
  handlerRef.current = handler;

  useEffect(() => {
    if (!connection) return;

    const stableHandler = (payload: T) => handlerRef.current(payload);
    connection.on(eventName, stableHandler);

    return () => {
      connection.off(eventName, stableHandler);
    };
  }, [connection, eventName]);
}
